import { TextElement } from './TextElement';
import { vec3 } from 'gl-matrix';
import { TextManager } from './TextManager';
import { Context } from '../context/Context';
import { Camera } from '../camera';
import { RenderSettings } from '../renderer';
import { project } from '../math';


export class DOMTextElement extends TextElement {
  public element: HTMLSpanElement;
  private lastWindowPosition?: vec3;
  private threshold: number;

  constructor(
    textManager: TextManager,
    inWorldSpace: boolean = true,
    centerText: boolean = true,
    cssClass?: string,
  ) {
    super(textManager, inWorldSpace, centerText);

    this.threshold = 0.5;
    this.element = document.createElement('span');
    this.element.style.position = 'absolute';
    this.element.setAttribute('class', cssClass ? cssClass : 'default');
  }

  public override get text() {
    return this._text;
  }

  public override set text(str: string) {
    this._text = str;
    this.element.textContent = str;
  }

  public destroy() {
    this.textManager.removeText(this);
    this.element.parentNode?.removeChild(this.element);
  }

  public render(ctx: Context, camera: Camera, settings: RenderSettings): void {
    let pos = vec3.clone(this.position);
    vec3.add(pos, pos, this.offset);

    // Use display dimensions (CSS pixels) for DOM positioning, not render resolution
    const displayDims = {
      width: ctx.viewPort.displayWidth,
      height: ctx.viewPort.displayHeight,
    };

    let textPosition: vec3;
    if (this.inWorldSpace) {
      // Calculate the text position in 3D and convert it back to a 2D position relative to the screen (which is used in ortho mode)
      let projected = project(pos, camera.projectionMatrix, camera.modelViewMatrix, displayDims);

      textPosition = vec3.fromValues(projected[0], projected[1], projected[2]);
    } else {
      textPosition = pos;
    }

    let windowPosition = this.convertPositionToWindowPosition(displayDims, textPosition);

    // Only update text position when it has actually changed (i.e. moved more than <threshold> pixels).
    // This draw method can be called up to 60 times per second, it would be a significant bottleneck if
    // the browser has to micro adjust the position of the text every frame.
    if (this.differs(windowPosition)) {
      this.lastWindowPosition = windowPosition;
      if (this.centerText) {
        this.element.style.left = windowPosition[0] - this.element.offsetWidth / 2 + 'px';
      } else {
        this.element.style.left = windowPosition[0] + 'px';
      }
      this.element.style.top = windowPosition[1] + 'px';
      this.element.style.display = windowPosition[2] > 1.0 ? 'none' : 'inline-block';
    }
  }

  private differs(windowPosition: vec3): boolean {
    if (this.lastWindowPosition) {
      if (Math.abs(this.lastWindowPosition[0] - windowPosition[0]) >= this.threshold) {
        return true;
      }
      if (Math.abs(this.lastWindowPosition[1] - windowPosition[1]) >= this.threshold) {
        return true;
      }

      return windowPosition[2] > 1.0 && this.element.style.display !== 'none';
    } else {
      return true;
    }
  }

  private convertPositionToWindowPosition(
    dims: { width: number; height: number },
    textPosition: vec3,
  ): vec3 {
    if (this.inWorldSpace) {
      return vec3.fromValues(textPosition[0], dims.height - textPosition[1], textPosition[2]);
    }
    return vec3.fromValues(
      textPosition[0] + dims.width / 2,
      dims.height - (textPosition[1] + dims.height / 2),
      textPosition[2],
    );
  }
}
